import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield, Crown, Lock, Unlock, Copy, Check, Share2, Radar,
  TrendingUp, Users, Target, Sparkles, ArrowLeft, Trophy,
  Activity, Eye, ShieldCheck, Download, X as XIcon,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { ShadowFeed } from "../components/ShadowFeed";
import { RankingWidget } from "../components/RankingWidget";
import { ShadowChat } from "../components/ShadowChat";
import LedgerShadowBanner from "../components/LedgerShadowBanner";
import { TopNewOperatorsWidget } from "../components/shadow/TopNewOperatorsWidget";
import { SentimentWidget } from "../components/shadow/SentimentWidget";
import { DailyBriefWidget } from "../components/shadow/DailyBriefWidget";
import { PremiosBanner } from "../components/shadow/PremiosBanner";
import ProGate from "../components/shadow/ProGate";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { getShadowI18n } from "../i18n/shadow";
import { trackCta } from "../lib/analytics";

import { API, safeJson } from "../lib/api";
// API now resolved at runtime via /lib/api.js
const RANK_COLOR = {
  RECRUIT: { fg: "text-slate-300", bg: "bg-slate-500/10", ring: "ring-slate-400/30", glow: "shadow-[0_0_24px_rgba(148,163,184,0.25)]" },
  OPERATOR: { fg: "text-cyan-300", bg: "bg-cyan-500/10", ring: "ring-cyan-400/40", glow: "shadow-[0_0_28px_rgba(34,211,238,0.35)]" },
  SHADOW_ANALYST: { fg: "text-violet-300", bg: "bg-violet-500/10", ring: "ring-violet-400/40", glow: "shadow-[0_0_30px_rgba(167,139,250,0.4)]" },
  LIQUIDITY_HUNTER: { fg: "text-emerald-300", bg: "bg-emerald-500/10", ring: "ring-emerald-400/40", glow: "shadow-[0_0_32px_rgba(52,211,153,0.4)]" },
  MARKET_COMMANDER: { fg: "text-orange-300", bg: "bg-orange-500/10", ring: "ring-orange-400/40", glow: "shadow-[0_0_34px_rgba(251,146,60,0.45)]" },
  REBEL_ELITE: { fg: "text-amber-300", bg: "bg-amber-500/10", ring: "ring-amber-400/50", glow: "shadow-[0_0_42px_rgba(251,191,36,0.55)]" },
};

// ------------------------------------------------------------
// Tactical radar (pure CSS animated SVG, no canvas needed)
// ------------------------------------------------------------
const RadarScan = ({ label }) => (
  <div className="relative h-32 w-32" data-testid="shadow-radar">
    <div className="absolute inset-0 rounded-full border border-emerald-400/30 bg-black/50" />
    <div className="absolute inset-2 rounded-full border border-emerald-400/20" />
    <div className="absolute inset-5 rounded-full border border-emerald-400/15" />
    <div className="absolute inset-9 rounded-full border border-emerald-400/10" />
    <div className="absolute left-1/2 top-1/2 h-1 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-300 shadow-[0_0_10px_rgba(52,211,153,0.9)]" />
    {/* Rotating sweep beam */}
    <div
      className="absolute inset-0 origin-center animate-[spin_4s_linear_infinite]"
      style={{
        background:
          "conic-gradient(from 0deg, rgba(52,211,153,0.45) 0deg, rgba(52,211,153,0.15) 30deg, transparent 60deg, transparent 360deg)",
        borderRadius: "9999px",
        maskImage: "radial-gradient(circle, black 60%, transparent 70%)",
      }}
    />
    <span className="absolute -bottom-5 left-0 right-0 text-center text-[9px] font-mono uppercase tracking-[0.22em] text-emerald-300/70">
      {label}
    </span>
  </div>
);

const StatusPill = ({ status, ui }) => {
  const map = {
    pending: { label: ui.status_pending, cls: "border-white/15 bg-white/[0.04] text-white/60" },
    active: { label: ui.status_active, cls: "border-emerald-400/40 bg-emerald-400/10 text-emerald-200" },
    converted: { label: ui.status_converted, cls: "border-amber-400/50 bg-amber-400/15 text-amber-200" },
  };
  const m = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] ${m.cls}`}>
      <span className="h-1 w-1 rounded-full bg-current" />
      {m.label}
    </span>
  );
};

// ------------------------------------------------------------
// Main page
// ------------------------------------------------------------
const ShadowNetworkPage = () => {
  const { lang } = useLanguage();
  const ui = getShadowI18n(lang);
  const { user, token, openLogin } = useAuth();
  const [me, setMe] = useState(null);
  const [network, setNetwork] = useState({ items: [], count: 0 });
  const [leaderboard, setLeaderboard] = useState({ items: [] });
  const [tab, setTab] = useState("overview");
  const [copied, setCopied] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareChannel, setShareChannel] = useState(null); // pre-selected platform when opening
  const [shareCopied, setShareCopied] = useState(false);

  const authed = user && user !== false;

  // Load operator profile + network + leaderboard
  useEffect(() => {
    let alive = true;

    // Leaderboard is public — always load
    (async () => {
      try {
        const res = await fetch(`${API}/shadow/leaderboard?limit=25`);
        if (!res.ok) throw new Error(`shadow/leaderboard ${res.status}`);
        const data = await safeJson(res);
        if (alive) setLeaderboard(data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[shadow] leaderboard failed", e);
      }
    })();

    // Authed-only fetches — split so a failure in one doesn't kill the other
    if (authed && token) {
      (async () => {
        try {
          const res = await fetch(`${API}/shadow/me`, {
            credentials: "include",
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) throw new Error(`shadow/me ${res.status}`);
          const data = await safeJson(res);
          if (alive) setMe(data);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error("[shadow] /me failed", e);
        }
      })();
      (async () => {
        try {
          const res = await fetch(`${API}/shadow/network`, {
            credentials: "include",
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) throw new Error(`shadow/network ${res.status}`);
          const data = await safeJson(res);
          if (alive) setNetwork(data);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error("[shadow] /network failed", e);
        }
      })();
    }

    return () => { alive = false; };
  }, [authed, token]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const copyLink = () => {
    if (!me?.invite_link) return;
    try {
      navigator.clipboard.writeText(me.invite_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      trackCta("shadow_copy_link", { invite_code: me.invite_code });
    } catch {}
  };

  // Poster image attached to every social share post (X, FB, IG, WhatsApp, Telegram).
  const SHARE_POSTER =
    "https://customer-assets.emergentagent.com/job_polyglot-trendia-web/artifacts/iq86or1f_ChatGPT%20Image%2029%20may%202026%2C%2011_45_11.png";

  // Branded broadcast message — user-approved copy.
  // Replaces the generic rebelbtc.com URL with the operator's personal invite_link
  // so referral tracking is preserved across every channel.
  const buildShareMessage = (inviteLink) =>
    `🚨 El mercado no premia a los rápidos.\n\nPremia a los preparados.\n\n🎓 Academia Gratis\n🤖 Mentor IA\n📈 Market Simulator™\n🕶️ Shadow Network™\n\n👉 ${inviteLink}\n\n#Bitcoin #Crypto #Trading`;

  const share = (channel) => {
    if (!me?.invite_link) return;
    // Unified share flow: every channel opens the same modal so the user
    // always has the option to download the poster + copy the branded caption,
    // and then deep-link out to the chosen platform.
    setShareChannel(channel || null);
    setShareOpen(true);
    trackCta(`shadow_share_${channel}`, { invite_code: me.invite_code });
  };

  // Deep-link out to a specific social platform (called from inside the modal)
  const openPlatform = (channel) => {
    if (!me?.invite_link) return;
    const inviteLink = me.invite_link;
    const message = buildShareMessage(inviteLink);
    const text = encodeURIComponent(message);
    const url = encodeURIComponent(inviteLink);
    const links = {
      x:        `https://twitter.com/intent/tweet?text=${text}`,
      telegram: `https://t.me/share/url?url=${url}&text=${text}`,
      whatsapp: `https://api.whatsapp.com/send?text=${text}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`,
    };
    const target = links[channel];
    if (!target) return; // Instagram has no web share intent
    trackCta(`shadow_share_open_${channel}`, { invite_code: me.invite_code });
    window.open(target, "_blank", "noopener,noreferrer");
  };

  // Share-modal helpers (used by all platforms)
  const shareCopyText = async () => {
    if (!me?.invite_link) return;
    try {
      await navigator.clipboard.writeText(buildShareMessage(me.invite_link));
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 1800);
    } catch {}
  };
  const shareDownload = async () => {
    // Strategy:
    //  1. Try Web Share API Level 2 (mobile native sheet WITH the image attached).
    //     Works on iOS Safari 15+, Android Chrome. Lets the user save to Photos,
    //     send via any app, etc. — exactly the experience the user expected.
    //  2. Fallback to fetch + blob download (works on desktop / CORS-friendly hosts).
    //  3. Last resort: open the poster in a new tab so the user can long-press / save.
    const downloadName = "rebel-shadow-share.png";
    try {
      const res = await fetch(SHARE_POSTER, { mode: "cors", cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();

      // 1) Native share sheet with image attached
      const file = new File([blob], downloadName, { type: blob.type || "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: ui.share_native_title,
            text: me?.invite_link ? buildShareMessage(me.invite_link) : undefined,
          });
          return;
        } catch (err) {
          // user cancelled — fall through to blob download as backup
        }
      }

      // 2) Blob download
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = downloadName;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
    } catch {
      // 3) Last resort
      window.open(SHARE_POSTER, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="min-h-screen bg-black text-white antialiased" data-testid="shadow-network-page">
      <Navbar />
      <PremiosBanner lang={lang} />

      {/* HERO */}
      <section className="relative w-full overflow-hidden pb-10 pt-32 md:pt-40">
        {/* Tactical grid + radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(34,211,238,0.10),transparent_55%),radial-gradient(circle_at_85%_30%,rgba(52,211,153,0.07),transparent_45%)]" />
        <div className="absolute inset-x-0 top-0 mx-auto h-px max-w-5xl bg-gradient-to-r from-transparent via-emerald-300/30 to-transparent" />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(52,211,153,0.4) 1px, transparent 1px), linear-gradient(to bottom, rgba(52,211,153,0.4) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
            maskImage: "radial-gradient(circle at 50% 0%, black, transparent 70%)",
          }}
        />
        {/* Scanline overlay */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, rgba(255,255,255,1) 0px, rgba(255,255,255,1) 1px, transparent 1px, transparent 4px)",
          }}
        />

        <div className="relative mx-auto max-w-6xl px-6 md:px-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.22em] text-white/40 transition-colors hover:text-emerald-300"
            data-testid="shadow-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            // RETURN TO BASE
          </Link>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-black/40 px-3 py-1.5 backdrop-blur">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.9)]" />
              <span className="text-[10px] font-mono font-bold uppercase tracking-[0.32em] text-emerald-300">
                {ui.eyebrow}
              </span>
            </div>
            <div className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 backdrop-blur">
              <ShieldCheck className="h-3 w-3 text-white/50" />
              <span className="text-[10px] font-mono uppercase tracking-[0.28em] text-white/50">
                {ui.classified_seal}
              </span>
            </div>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-5 font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl"
            data-testid="shadow-title"
          >
            {ui.hero_title}
          </motion.h1>

          <p className="mt-3 max-w-2xl text-base leading-relaxed text-white/55 md:text-lg">
            {ui.hero_sub}
          </p>

          {/* Tabs */}
          <div className="mt-10 inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] p-1 backdrop-blur" data-testid="shadow-tabs">
            {[
              { id: "overview", label: ui.tab_overview, Icon: Eye },
              { id: "network", label: ui.tab_network, Icon: Users },
              { id: "leaderboard", label: ui.tab_leaderboard, Icon: Trophy },
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                data-testid={`shadow-tab-${id}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-[0.2em] transition-all ${
                  tab === id
                    ? "bg-emerald-400/15 text-emerald-200 ring-1 ring-emerald-400/40"
                    : "text-white/45 hover:text-white"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="relative w-full pb-32">
        <div className="mx-auto max-w-6xl px-6 md:px-12">
          {/* GUEST GATE */}
          {!authed && tab !== "leaderboard" && (
            <GuestGate ui={ui} onLogin={openLogin} />
          )}

          {/* OVERVIEW */}
          {authed && tab === "overview" && me && (
            <OverviewPanel me={me} ui={ui} copied={copied} copyLink={copyLink} share={share} />
          )}

          {/* NETWORK */}
          {authed && tab === "network" && me && (
            <NetworkPanel network={network} ui={ui} me={me} copyLink={copyLink} copied={copied} />
          )}

          {/* LEADERBOARD (public for view, but ranking is PRO per spec) */}
          {tab === "leaderboard" && (
            <ProGate feature="ranking">
              <LeaderboardPanel data={leaderboard} ui={ui} myCodename={me?.operator_codename} />
            </ProGate>
          )}
        </div>
      </section>

      {/* Universal share modal — same flow for X, Facebook, Telegram, WhatsApp, Instagram.
          Every channel always offers: download poster + copy caption + deep-link out. */}
      <AnimatePresence>
        {shareOpen && me?.invite_link && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9000] flex items-center justify-center overflow-y-auto bg-black/85 p-3 backdrop-blur-md sm:p-4"
            onClick={() => setShareOpen(false)}
            data-testid="shadow-share-modal"
          >
            <motion.div
              initial={{ scale: 0.92, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.92, y: 20, opacity: 0 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative flex max-h-[92vh] w-full max-w-md flex-col overflow-hidden rounded-3xl border border-orange-400/30 bg-gradient-to-br from-zinc-950 via-zinc-950 to-orange-950/20 shadow-[0_0_60px_rgba(255,140,0,0.18)]"
            >
              <button
                onClick={() => setShareOpen(false)}
                aria-label="close"
                className="absolute right-4 top-4 z-10 rounded-full border border-white/15 bg-black/70 p-1.5 text-white/80 backdrop-blur transition-colors hover:border-white/40 hover:text-white"
                data-testid="shadow-share-close"
              >
                <XIcon className="h-4 w-4" />
              </button>

              {/* Scrollable content area — keeps the modal within viewport on every screen size */}
              <div className="flex-1 overflow-y-auto overscroll-contain p-6">
                <p className="pr-10 text-[10px] font-mono uppercase tracking-[0.3em] text-orange-300/80">
                  {ui.share_kit} · {shareChannel ? shareChannel.toUpperCase() : ui.share_all_networks}
                </p>
                <h3 className="mt-1 text-xl font-black text-white">
                  {ui.share_modal_title}
                </h3>
                <p className="mt-1.5 text-xs text-white/60">
                  {ui.share_modal_sub}
                </p>

                {/* Poster preview — bounded so it never dominates the viewport */}
                <div className="mt-4 overflow-hidden rounded-2xl border border-white/10 bg-black/40">
                  <img
                    src={SHARE_POSTER}
                    alt="REBEL share poster"
                    className="mx-auto block h-auto w-full object-contain"
                    style={{ maxHeight: "min(46vh, 360px)" }}
                    loading="lazy"
                  />
                </div>

                {/* Universal actions: download + copy */}
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <button
                    onClick={shareCopyText}
                    data-testid="shadow-share-copy"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-3 py-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white transition-all hover:-translate-y-0.5 hover:border-white/40 hover:bg-white/[0.08]"
                  >
                    {shareCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {shareCopied ? ui.ig_modal_copied : ui.ig_modal_copy}
                  </button>
                  <button
                    onClick={shareDownload}
                    data-testid="shadow-share-download"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-amber-600 px-3 py-2.5 text-[11px] font-black uppercase tracking-[0.18em] text-black shadow-[0_8px_24px_rgba(255,140,0,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_36px_rgba(255,140,0,0.55)]"
                  >
                    <Download className="h-3.5 w-3.5" />
                    {ui.ig_modal_download}
                  </button>
                </div>

                {/* Platform deep-links */}
                <div className="mt-5">
                  <p className="text-[9px] font-mono uppercase tracking-[0.28em] text-white/40">
                    {ui.share_open_in}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {[
                      { id: "x",        label: "X / Twitter" },
                      { id: "facebook", label: "Facebook" },
                      { id: "telegram", label: "Telegram" },
                      { id: "whatsapp", label: "WhatsApp" },
                    ].map((p) => (
                      <button
                        key={p.id}
                        onClick={() => openPlatform(p.id)}
                        data-testid={`shadow-share-open-${p.id}`}
                        className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white/70 transition-all hover:-translate-y-0.5 hover:border-orange-400/50 hover:bg-orange-400/[0.08] hover:text-orange-200"
                      >
                        {p.label}
                      </button>
                    ))}
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full border border-pink-400/30 bg-pink-400/[0.06] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-pink-200"
                      title={ui.share_instagram_title}
                    >
                      {ui.share_instagram_manual}
                    </span>
                  </div>
                </div>

                {/* Caption preview */}
                <div className="mt-4 rounded-2xl border border-white/5 bg-black/40 p-3">
                  <p className="text-[9px] font-mono uppercase tracking-[0.24em] text-white/35">
                    {ui.share_caption_preview}
                  </p>
                  <pre className="mt-1.5 whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-white/75">
{buildShareMessage(me.invite_link)}
                  </pre>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Shadow Network Posts feed — ADDITIVE block (does not modify the
          existing network/leaderboard tabs above). PRO publishes, all can view.
          Side widget shows the SHADOW RANKING™ top 5 (additive). */}
      <section className="relative mx-auto w-full max-w-[1400px] px-4 pb-10 pt-6 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_300px]">
          <div className="min-w-0">
            <ShadowFeed />
          </div>
          <div className="order-first space-y-4 lg:order-none lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:pr-1 sidebar-scroll" data-testid="shadow-sidebar">
            <SentimentWidget lang={lang} />
            <DailyBriefWidget />
            <ProGate feature="ranking" compact>
              <RankingWidget />
            </ProGate>
            <TopNewOperatorsWidget />
            <LedgerShadowBanner />
            <div className="hidden lg:block">
              <ProGate feature="chat" compact>
                <ShadowChat />
              </ProGate>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* SHADOW CHAT — mobile/tablet fixed-bottom mount (additive). On lg+ the
          chat appears in the sticky sidebar above. */}
      <div className="lg:hidden">
        <ProGate feature="chat" compact>
          <ShadowChat />
        </ProGate>
      </div>
    </div>
  );
};

// ------------------------------------------------------------
// Guest gate
// ------------------------------------------------------------
const GuestGate = ({ ui, onLogin }) => (
  <div className="mx-auto max-w-2xl rounded-3xl border border-emerald-400/20 bg-gradient-to-br from-emerald-500/[0.04] via-transparent to-cyan-500/[0.04] p-10 text-center backdrop-blur-xl" data-testid="shadow-guest-gate">
    <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full border border-emerald-400/40 bg-black/40">
      <Lock className="h-6 w-6 text-emerald-300" />
    </div>
    <h3 className="mt-5 font-display text-2xl font-bold tracking-tight text-white md:text-3xl">
      {ui.join_alert_title}
    </h3>
    <p className="mt-3 text-sm leading-relaxed text-white/55 md:text-base">{ui.join_alert_msg}</p>
    <button
      onClick={onLogin}
      data-testid="shadow-guest-login-btn"
      className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-400 px-6 py-3 text-xs font-black uppercase tracking-[0.22em] text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(52,211,153,0.55)]"
    >
      <ShieldCheck className="h-4 w-4" />
      {ui.join_cta}
    </button>
  </div>
);

// ------------------------------------------------------------
// Overview
// ------------------------------------------------------------
const OverviewPanel = ({ me, ui, copied, copyLink, share }) => {
  const rc = RANK_COLOR[me.rank.current.id] || RANK_COLOR.RECRUIT;
  const rankLabel = ui[`rank_${me.rank.current.id}`] || me.rank.current.label;
  const nextLabel = me.rank.next ? (ui[`rank_${me.rank.next.id}`] || me.rank.next.label) : null;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3" data-testid="shadow-overview">
      {/* LEFT: Identity card */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent p-6 ring-1 ${rc.ring} ${rc.glow} lg:col-span-1`}
      >
        {/* Corner ticks (tactical aesthetic) */}
        <div className="absolute left-3 top-3 h-3 w-3 border-l border-t border-emerald-400/40" />
        <div className="absolute right-3 top-3 h-3 w-3 border-r border-t border-emerald-400/40" />
        <div className="absolute bottom-3 left-3 h-3 w-3 border-b border-l border-emerald-400/40" />
        <div className="absolute bottom-3 right-3 h-3 w-3 border-b border-r border-emerald-400/40" />

        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300/80">
          {ui.profile_title}
        </p>

        <div className="mt-4 flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40">
              {ui.codename_label}
            </p>
            <h2 className="mt-1 font-mono text-2xl font-black tracking-tight text-white md:text-3xl" data-testid="shadow-codename">
              {me.operator_codename}
            </h2>
          </div>
          <RadarScan label={ui.radar_label} />
        </div>

        {/* Rank chip */}
        <div className="mt-5 flex items-center justify-between gap-3">
          <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 ${rc.ring} ${rc.bg}`}>
            <Crown className={`h-3.5 w-3.5 ${rc.fg}`} />
            <span className={`text-[10px] font-bold uppercase tracking-[0.24em] ${rc.fg}`} data-testid="shadow-rank-label">
              {rankLabel}
            </span>
          </div>
          {me.is_pro && (
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.22em] text-amber-200">
              <Sparkles className="h-3 w-3" /> {ui.badge_pro}
            </span>
          )}
        </div>

        {/* Rank progress */}
        <div className="mt-5">
          <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">
            <span>{ui.rank_progress}</span>
            <span>{me.rank.xp.toLocaleString()} XP</span>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/5">
            <div
              className="h-full bg-gradient-to-r from-emerald-400 via-cyan-300 to-emerald-400 transition-all"
              style={{ width: `${me.rank.progress_pct}%` }}
            />
          </div>
          {nextLabel ? (
            <p className="mt-2 text-[11px] text-white/40">
              → <span className="text-white/70">{nextLabel}</span> · {me.rank.xp_to_next.toLocaleString()} {ui.xp_to_next}
            </p>
          ) : (
            <p className="mt-2 text-[11px] text-amber-300">{ui.max_rank}</p>
          )}
        </div>
      </motion.div>

      {/* RIGHT TOP: Invite + share */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="lg:col-span-2 lg:row-span-2 lg:flex lg:flex-col lg:gap-6"
      >
        <div className="relative overflow-hidden rounded-3xl border border-cyan-400/20 bg-gradient-to-br from-cyan-500/[0.04] to-transparent p-6 backdrop-blur-xl">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-300/80">
            {ui.link_label}
          </p>
          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
            <code className="flex-1 truncate rounded-2xl border border-white/10 bg-black/40 px-4 py-3 font-mono text-sm text-cyan-100" data-testid="shadow-invite-link">
              {me.invite_link}
            </code>
            <button
              onClick={copyLink}
              data-testid="shadow-copy-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-cyan-400/40 bg-cyan-400/10 px-5 py-3 text-xs font-bold uppercase tracking-[0.22em] text-cyan-200 transition-all hover:-translate-y-0.5 hover:bg-cyan-400/20"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? ui.copied : ui.copy}
            </button>
          </div>
          {/* Share row */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/40">
              <Share2 className="mr-1 inline h-3 w-3" /> {ui.cta_share_label}
            </span>
            {[
              { id: "x", label: ui.share_via_x },
              { id: "telegram", label: ui.share_via_telegram },
              { id: "whatsapp", label: ui.share_via_whatsapp },
              { id: "facebook", label: ui.share_via_facebook },
              { id: "instagram", label: ui.share_via_instagram },
            ].map((s) => (
              <button
                key={s.id}
                onClick={() => share(s.id)}
                data-testid={`shadow-share-${s.id}`}
                className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white/60 transition-colors hover:border-emerald-400/40 hover:text-emerald-200"
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Stats grid */}
        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4 lg:mt-0">
          <StatCard Icon={Activity} value={me.xp.toLocaleString()} label={ui.stats_xp} accent="emerald" />
          <StatCard Icon={TrendingUp} value={me.reputation.toLocaleString()} label={ui.stats_reputation} accent="cyan" />
          <StatCard Icon={Users} value={me.recruits.active} label={ui.stats_active} accent="violet" />
          <StatCard Icon={Target} value={me.recruits.converted} label={ui.stats_converted} accent="amber" />
        </div>

        {/* Unlocks */}
        <div className="mt-6">
          <div className="mb-4">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-300/80">
              {ui.unlocks_title}
            </p>
            <p className="mt-1 text-xs text-white/45">{ui.unlocks_sub}</p>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {me.unlocks.map((u) => (
              <UnlockCard key={u.id} unlock={u} ui={ui} />
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const StatCard = ({ Icon, value, label, accent }) => {
  const palettes = {
    emerald: "border-emerald-400/20 bg-emerald-400/5 text-emerald-200",
    cyan: "border-cyan-400/20 bg-cyan-400/5 text-cyan-200",
    violet: "border-violet-400/20 bg-violet-400/5 text-violet-200",
    amber: "border-amber-400/20 bg-amber-400/5 text-amber-200",
  };
  return (
    <div className={`relative overflow-hidden rounded-2xl border ${palettes[accent]} p-4 backdrop-blur`}>
      <Icon className="h-4 w-4 opacity-70" />
      <p className="mt-2 font-mono text-2xl font-black tracking-tight">{value}</p>
      <p className="mt-1 text-[10px] font-mono uppercase tracking-[0.22em] text-white/45">{label}</p>
    </div>
  );
};

const UnlockCard = ({ unlock, ui }) => {
  const unlocked = unlock.unlocked;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className={`relative flex items-center justify-between gap-4 rounded-2xl border p-4 backdrop-blur ${
        unlocked
          ? "border-amber-400/40 bg-amber-400/[0.06]"
          : "border-white/10 bg-white/[0.025]"
      }`}
      data-testid={`shadow-unlock-${unlock.id}`}
    >
      <div className="flex items-center gap-3">
        <div className={`flex h-9 w-9 items-center justify-center rounded-xl border ${
          unlocked ? "border-amber-400/40 bg-amber-400/15 text-amber-300" : "border-white/10 bg-black/30 text-white/40"
        }`}>
          {unlocked ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
        </div>
        <div>
          <p className={`text-sm font-bold ${unlocked ? "text-amber-100" : "text-white/80"}`}>
            {unlock.label}
          </p>
          <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/40">
            {unlock.active_required} OPS · {unlocked ? ui.unlocked : `${unlock.remaining} ${ui.invites_needed}`}
          </p>
        </div>
      </div>
      {unlocked && (
        <span className="rounded-full border border-amber-400/50 bg-amber-400/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.22em] text-amber-200">
          ✓ {ui.unlocked}
        </span>
      )}
    </motion.div>
  );
};

// ------------------------------------------------------------
// Network
// ------------------------------------------------------------
const NetworkPanel = ({ network, ui }) => {
  return (
    <div data-testid="shadow-network-panel">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300/80">
            {ui.network_title}
          </p>
          <p className="mt-1 text-xs text-white/45">{network.count} OPS · {ui.classified_seal}</p>
        </div>
      </div>

      {network.items.length === 0 ? (
        <div className="rounded-3xl border border-white/10 bg-white/[0.025] p-10 text-center backdrop-blur-xl">
          <Radar className="mx-auto h-10 w-10 text-emerald-300/60" />
          <p className="mt-4 text-sm text-white/55">{ui.network_empty}</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.025] backdrop-blur-xl">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 bg-white/[0.02]">
              <tr className="text-left text-[10px] font-mono uppercase tracking-[0.22em] text-white/40">
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">{ui.th_codename}</th>
                <th className="px-4 py-3">XP</th>
                <th className="px-4 py-3">{ui.joined_at}</th>
                <th className="px-4 py-3 text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {network.items.map((it, idx) => (
                <tr key={idx} className="border-t border-white/5 transition-colors hover:bg-white/[0.02]">
                  <td className="px-4 py-3 font-mono text-white/40">{String(idx + 1).padStart(3, "0")}</td>
                  <td className="px-4 py-3">
                    <p className="font-mono text-white">{it.codename}</p>
                    <p className="text-[10px] text-white/30">{it.handle}</p>
                  </td>
                  <td className="px-4 py-3 font-mono text-white/70">{(it.xp || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-[11px] text-white/40">
                    {it.joined_at ? new Date(it.joined_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <StatusPill status={it.status} ui={ui} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------
// Leaderboard
// ------------------------------------------------------------
const LeaderboardPanel = ({ data, ui, myCodename }) => {
  return (
    <div data-testid="shadow-leaderboard-panel">
      <div className="mb-6">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-300/80">
          {ui.leaderboard_title}
        </p>
        <p className="mt-1 text-xs text-white/45">{ui.leaderboard_sub}</p>
      </div>

      <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.025] backdrop-blur-xl">
        <table className="w-full text-sm">
          <thead className="border-b border-white/10 bg-white/[0.02]">
            <tr className="text-left text-[10px] font-mono uppercase tracking-[0.22em] text-white/40">
              <th className="px-4 py-3">{ui.th_rank}</th>
              <th className="px-4 py-3">{ui.th_codename}</th>
              <th className="px-4 py-3 text-right">{ui.th_xp}</th>
              <th className="px-4 py-3 text-right">{ui.th_rep}</th>
              <th className="hidden px-4 py-3 text-right md:table-cell">{ui.th_recruits}</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((row, idx) => {
              const rc = RANK_COLOR[row.rank_id] || RANK_COLOR.RECRUIT;
              const isMe = myCodename && row.codename === myCodename;
              return (
                <tr
                  key={idx}
                  className={`border-t border-white/5 transition-colors ${
                    isMe ? "bg-emerald-400/[0.04]" : "hover:bg-white/[0.02]"
                  }`}
                  data-testid={`shadow-lb-row-${idx}`}
                >
                  <td className="px-4 py-3 font-mono text-white/50">
                    {idx === 0 ? <Crown className="inline h-4 w-4 text-amber-300" /> : null}
                    {" "}{String(idx + 1).padStart(2, "0")}
                  </td>
                  <td className="px-4 py-3">
                    <p className={`font-mono font-bold ${rc.fg} ${isMe ? "text-emerald-200" : ""}`}>
                      {row.codename}
                      {isMe && (
                        <span className="ml-2 inline-flex items-center rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2 py-0.5 text-[8px] font-bold uppercase tracking-[0.22em] text-emerald-200">
                          YOU
                        </span>
                      )}
                    </p>
                    <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/40">
                      {ui[`rank_${row.rank_id}`] || row.rank_label}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-white/80">{row.xp.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-mono text-amber-200">{row.reputation.toLocaleString()}</td>
                  <td className="hidden px-4 py-3 text-right text-[11px] text-white/50 md:table-cell">
                    {row.active_recruits} / {row.converted_recruits} <span className="text-white/30">PRO</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ShadowNetworkPage;
